import {
  useState,
  useEffect,
  useRef,
  useMemo,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { Layout, Menu, Avatar, Dropdown, Badge, Typography, Button, Modal, notification, Tag } from 'antd';
import {
  DashboardOutlined,
  CarOutlined,
  UserOutlined,
  FileTextOutlined,
  CalendarOutlined,
  AlertOutlined,
  TeamOutlined,
  AuditOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  NotificationOutlined,
  MailOutlined,
  SettingOutlined,
  ImportOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  supabase,
  useAuthUser,
  signOut,
  parseUserRole,
  isAdminRole,
  isFleetOwnerRole,
  useRealtimeChannel,
  usePortalPermissions,
  useFleetOperator,
  ROUTE_VIEW_PERMISSION,
} from '@ubus/shared';
import type { SOSEvent } from '@ubus/shared';
import LanguageSwitcher from './LanguageSwitcher';
import ReminderInboxDrawer, {
  fetchAdminReminderUnread,
} from './ReminderInboxDrawer';
import { useDisplayTimezone } from './DisplayTimezoneProvider';
import { localeConfirm } from '../utils/localeModal';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

type LeafItem = {
  key: string;
  icon: ReactNode;
  labelKey: string;
};

type MenuGroup = {
  key: string;
  labelKey: string;
  children: LeafItem[];
};

/** Admin IA — grouped by workflow (HANDOFF-20260715). Labels via i18n nav.* */
const ADMIN_MENU_GROUPS: MenuGroup[] = [
  {
    key: 'grp-command',
    labelKey: 'nav.grpCommand',
    children: [
      { key: '/dashboard', icon: <DashboardOutlined />, labelKey: 'nav.dashboard' },
      { key: '/sos-events', icon: <AlertOutlined />, labelKey: 'nav.sosEvents' },
    ],
  },
  {
    key: 'grp-scheduling',
    labelKey: 'nav.grpScheduling',
    children: [
      { key: '/schedule-overview', icon: <CalendarOutlined />, labelKey: 'nav.scheduleOverview' },
      { key: '/jobs', icon: <UnorderedListOutlined />, labelKey: 'nav.jobs' },
      { key: '/trips', icon: <FileTextOutlined />, labelKey: 'nav.trips' },
      { key: '/fleet-availability', icon: <CarOutlined />, labelKey: 'nav.fleetAvailability' },
    ],
  },
  {
    key: 'grp-fleet',
    labelKey: 'nav.grpFleet',
    children: [
      { key: '/fleet-operators', icon: <TeamOutlined />, labelKey: 'nav.fleetOperators' },
      { key: '/vehicles', icon: <CarOutlined />, labelKey: 'nav.vehicles' },
      { key: '/drivers', icon: <UserOutlined />, labelKey: 'nav.drivers' },
      { key: '/vehicle-inspections', icon: <AuditOutlined />, labelKey: 'nav.vehicleInspections' },
      { key: '/organizations', icon: <TeamOutlined />, labelKey: 'nav.organizations' },
      { key: '/reminder-records', icon: <BellOutlined />, labelKey: 'nav.reminderRecords' },
    ],
  },
  {
    key: 'grp-clients-ops',
    labelKey: 'nav.grpClients',
    children: [
      { key: '/data-management', icon: <ImportOutlined />, labelKey: 'nav.dataManagement' },
    ],
  },
  {
    key: 'grp-system',
    labelKey: 'nav.grpSystem',
    children: [
      { key: '/roles', icon: <TeamOutlined />, labelKey: 'nav.roles' },
      { key: '/users', icon: <UserOutlined />, labelKey: 'nav.users' },
      { key: '/system-config', icon: <MailOutlined />, labelKey: 'nav.systemConfig' },
      { key: '/reminder-settings', icon: <BellOutlined />, labelKey: 'nav.reminderSettings' },
      { key: '/settings', icon: <SettingOutlined />, labelKey: 'nav.settings' },
    ],
  },
];

/** Fleet owner workspace — orders first. */
const OWNER_MENU_GROUPS: MenuGroup[] = [
  {
    key: 'grp-command',
    labelKey: 'nav.grpCommand',
    children: [
      { key: '/dashboard', icon: <DashboardOutlined />, labelKey: 'nav.dashboard' },
      { key: '/sos-events', icon: <AlertOutlined />, labelKey: 'nav.sosEvents' },
    ],
  },
  {
    key: 'grp-orders',
    labelKey: 'nav.grpOrders',
    children: [
      { key: '/schedule-overview', icon: <CalendarOutlined />, labelKey: 'nav.scheduleOverview' },
      { key: '/jobs', icon: <UnorderedListOutlined />, labelKey: 'nav.jobs' },
      { key: '/daily-schedule', icon: <ThunderboltOutlined />, labelKey: 'nav.dailySchedule' },
      { key: '/trips', icon: <FileTextOutlined />, labelKey: 'nav.trips' },
      { key: '/fleet-availability', icon: <CarOutlined />, labelKey: 'nav.fleetAvailability' },
    ],
  },
  {
    key: 'grp-fleet',
    labelKey: 'nav.grpFleet',
    children: [
      { key: '/vehicles', icon: <CarOutlined />, labelKey: 'nav.vehicles' },
      { key: '/drivers', icon: <UserOutlined />, labelKey: 'nav.drivers' },
      { key: '/vehicle-inspections', icon: <AuditOutlined />, labelKey: 'nav.vehicleInspections' },
      { key: '/fleet-profile', icon: <IdcardOutlined />, labelKey: 'nav.fleetProfile' },
      { key: '/team', icon: <UserOutlined />, labelKey: 'nav.teamMembers' },
      { key: '/owner-organizations', icon: <TeamOutlined />, labelKey: 'nav.ownerOrganizations' },
      { key: '/reminder-records', icon: <BellOutlined />, labelKey: 'nav.reminderRecords' },
    ],
  },
];

const LEAF_KEYS = [
  ...ADMIN_MENU_GROUPS.flatMap((g) => g.children.map((c) => c.key)),
  ...OWNER_MENU_GROUPS.flatMap((g) => g.children.map((c) => c.key)),
];

function selectedLeafKey(pathname: string): string {
  const matches = LEAF_KEYS.filter(
    (key) => pathname === key || pathname.startsWith(`${key}/`)
  );
  if (matches.length === 0) return '/dashboard';
  return matches.sort((a, b) => b.length - a.length)[0];
}

function toGroupItems(
  groups: MenuGroup[],
  sosCount: number,
  t: TFunction,
  canView: (path: string) => boolean
): MenuProps['items'] {
  return groups
    .map((group) => {
      const children = group.children
        .filter((leaf) => canView(leaf.key))
        .map((leaf) => {
          if (leaf.key === '/sos-events' && sosCount > 0) {
            return {
              key: leaf.key,
              label: t(leaf.labelKey),
              icon: (
                <Badge count={sosCount} size="small" offset={[4, 0]}>
                  <AlertOutlined />
                </Badge>
              ),
            };
          }
          return {
            key: leaf.key,
            icon: leaf.icon,
            label: t(leaf.labelKey),
          };
        });
      if (children.length === 0) return null;
      return {
        type: 'group' as const,
        key: group.key,
        label: t(group.labelKey),
        children,
      };
    })
    .filter(Boolean);
}

function roleLabelKey(role: ReturnType<typeof parseUserRole>): string {
  if (isFleetOwnerRole(role)) return 'common.roleOwner';
  if (isAdminRole(role)) return 'common.roleAdmin';
  return 'common.roleUser';
}

function AdminLayout() {
  const { t, i18n } = useTranslation();
  const { timezone } = useDisplayTimezone();
  const [collapsed, setCollapsed] = useState(false);
  const user = useAuthUser();
  const role = parseUserRole(user);
  const { operator } = useFleetOperator();
  const { hasPermission, access, loading: permLoading } = usePortalPermissions();
  const [sosCount, setSosCount] = useState(0);
  const [reminderUnread, setReminderUnread] = useState(0);
  const [reminderInboxOpen, setReminderInboxOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const notifiedSosIds = useRef<Set<string>>(new Set());
  const canViewSos = hasPermission('sos.view');
  const showSosBell = canViewSos;
  const canViewReminders = hasPermission('reminders.view');
  /** In-app header inbox is for fleet managers only — HQ uses settings/records without push bell. */
  const showReminderInbox = isFleetOwnerRole(role) && canViewReminders;

  useEffect(() => {
    if (permLoading) return;
    if (access?.status === 'disabled') {
      void signOut().then(() => navigate('/login', { replace: true }));
    }
  }, [access?.status, permLoading, navigate]);

  const fetchSosCount = async () => {
    try {
      const { count, error } = await supabase
        .from('sos_events')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'new');

      if (!error && count !== null) {
        setSosCount(count);
      }
    } catch (err) {
      console.error('Failed to fetch SOS count:', err);
    }
  };

  const fetchReminderUnread = async () => {
    if (!user?.id || !showReminderInbox) {
      setReminderUnread(0);
      return;
    }
    try {
      const { unreadIds } = await fetchAdminReminderUnread(user.id);
      setReminderUnread(unreadIds.length);
    } catch (err) {
      console.error('Failed to fetch reminder unread:', err);
    }
  };

  useEffect(() => {
    if (!canViewSos) {
      setSosCount(0);
      return;
    }
    void fetchSosCount();
  }, [canViewSos]);

  useEffect(() => {
    if (permLoading || !showReminderInbox || !user?.id) return;
    void fetchReminderUnread();
    const timer = window.setInterval(() => {
      void fetchReminderUnread();
    }, 60_000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poll when auth/perm ready
  }, [permLoading, showReminderInbox, user?.id]);

  useRealtimeChannel(
    'admin-layout-sos',
    [
      {
        event: '*',
        schema: 'public',
        table: 'sos_events',
        callback: (payload) => {
          if (!canViewSos) return;
          void fetchSosCount();
          if (payload.eventType !== 'INSERT') return;
          const newEvent = payload.new as unknown as SOSEvent;
          if (notifiedSosIds.current.has(newEvent.id)) return;
          notifiedSosIds.current.add(newEvent.id);

          notification.warning({
            message: t('sosToast.title'),
            description: t('sosToast.body', { type: newEvent.event_type }),
            placement: 'topRight',
            duration: 0,
            btn: (
              <Button
                type="primary"
                size="small"
                danger
                onClick={() => {
                  notification.destroy();
                  navigate('/sos-events');
                }}
              >
                {t('sosToast.open')}
              </Button>
            ),
          });
        },
      },
    ],
    [t, canViewSos]
  );

  const handleLogout = () => {
    localeConfirm(t, {
      title: t('common.logoutConfirmTitle'),
      content: t('common.logoutConfirmBody'),
      okText: t('common.logout'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        await signOut();
        navigate('/login');
      },
    });
  };

  const menuItems = useMemo(() => {
    const groups = isAdminRole(role) ? ADMIN_MENU_GROUPS : OWNER_MENU_GROUPS;
    const canView = (path: string) => {
      if (path === '/schedule-overview') {
        if (isAdminRole(role)) return hasPermission('jobs.view');
        if (isFleetOwnerRole(role)) return hasPermission('owner_schedule.view');
        return false;
      }
      const code = ROUTE_VIEW_PERMISSION[path];
      if (!code) return true;
      return hasPermission(code);
    };
    return toGroupItems(groups, canViewSos ? sosCount : 0, t, canView);
  }, [role, sosCount, t, i18n.language, hasPermission, canViewSos]);

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'account-security',
      icon: <SettingOutlined />,
      label: t('accountSecurity.menu'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('common.logout'),
      danger: true,
    },
  ];

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'account-security') {
      navigate('/account-security');
      return;
    }
    if (key === 'logout') {
      handleLogout();
    }
  };

  const selectedKey = selectedLeafKey(location.pathname);

  return (
    <Layout className="admin-shell">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={232}
        collapsedWidth={72}
        className="admin-sider"
      >
        <div className="admin-brand">
          <div className="admin-brand__icon">
            <CarOutlined />
          </div>
          {!collapsed && (
            <div>
              <span className="admin-brand__text">UBUS</span>
              <span className="admin-brand__tagline">{t('common.brandTagline')}</span>
            </div>
          )}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          className="admin-menu"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => {
            if (key.startsWith('/')) navigate(key);
          }}
        />
      </Sider>
      <Layout
        className="admin-main"
        style={
          {
            marginLeft: collapsed ? 72 : 232,
            ['--admin-sider-width' as string]: collapsed ? '72px' : '232px',
          } as CSSProperties
        }
      >
        <Header className="admin-header">
          <Button
            type="text"
            aria-label={collapsed ? t('common.expandSidebar') : t('common.collapseSidebar')}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />
          <div className="admin-header__actions">
            <LanguageSwitcher size="small" />
            {showReminderInbox ? (
              <Badge count={reminderUnread} size="small">
                <NotificationOutlined
                  className="admin-header__bell"
                  onClick={() => setReminderInboxOpen(true)}
                  role="button"
                  tabIndex={0}
                  aria-label={t('reminders.headerBell')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setReminderInboxOpen(true);
                    }
                  }}
                />
              </Badge>
            ) : null}
            {showSosBell ? (
              <Badge
                count={sosCount}
                size="small"
                className={sosCount > 0 ? 'admin-sos-badge' : undefined}
              >
                <BellOutlined
                  className="admin-header__bell"
                  onClick={() => navigate('/sos-events')}
                  role="button"
                  tabIndex={0}
                  aria-label={t('common.sosNotifications')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate('/sos-events');
                    }
                  }}
                />
              </Badge>
            ) : null}
            <Tag color={isFleetOwnerRole(role) ? 'blue' : 'geekblue'} className="admin-header__role">
              {isFleetOwnerRole(role) && operator?.name
                ? `${t(roleLabelKey(role))} · ${operator.name}`
                : t(roleLabelKey(role))}
            </Tag>
            <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }} placement="bottomRight">
              <div
                className="admin-header__user"
                role="button"
                tabIndex={0}
                aria-label={t('common.accountMenu')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    (e.currentTarget as HTMLElement).click();
                  }
                }}
              >
                <Avatar style={{ backgroundColor: '#2563EB' }} icon={<UserOutlined />} />
                <Text>{user?.email || t('common.roleAdmin')}</Text>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content className="admin-content">
          <Outlet key={`${i18n.language}:${timezone}`} />
        </Content>
      </Layout>
      {showReminderInbox ? (
        <ReminderInboxDrawer
          open={reminderInboxOpen}
          onClose={() => {
            setReminderInboxOpen(false);
            void fetchReminderUnread();
          }}
          onUnreadChange={setReminderUnread}
        />
      ) : null}
    </Layout>
  );
}

export default AdminLayout;
